import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Play, ArrowRight, Shield, Clock, ChevronRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import TagPill from '../components/TagPill';
import GradientButton from '../components/GradientButton';
import podcastsData from '../data/podcasts.json';
import { useEpisodes, getLatestEpisode } from '../hooks/useEpisodes';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const HomePage = () => {
  const { episodes, loading } = useEpisodes();
  const latestEpisode = getLatestEpisode(episodes);
  const { podcastInfo, socialMedia } = podcastsData;

  // Testimonials data
  const testimonials = [
    {
      quote: "News from the Pew offers insightful commentary on the evolving landscape of American Christianity. A must-listen!",
      author: "Christian Today Magazine",
    },
    {
      quote: "A refreshing perspective on current events through the lens of Scripture.",
      author: "Faith & Family Review",
    },
    {
      quote: "Finally, a podcast that tackles the tough questions without compromising Biblical truth.",
      author: "Listener Review",
    },
  ];

  return (
    <>
      <Helmet>
        <title>News From The Pew | Faith, Culture & Current Events Podcast</title>
        <meta name="description" content="Theology with teeth. Unmasking the deception of the age with the uncompromised power of Scripture. Fresh perspectives on faith, culture, and current events." />
        <link rel="canonical" href="https://newsfromthepew.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://newsfromthepew.com/" />
        <meta property="og:title" content="News From The Pew | Faith, Culture & Current Events Podcast" />
        <meta property="og:description" content="Theology with teeth. Unmasking the deception of the age with the uncompromised power of Scripture." />
        <meta property="og:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="News From The Pew | Faith, Culture & Current Events Podcast" />
        <meta name="twitter:description" content="Theology with teeth. Unmasking the deception of the age with the uncompromised power of Scripture." />
        <meta name="twitter:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />

      <main>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-hero" />

          {/* Decorative elements */}
          <div className="hero-orb-primary top-20 left-10 w-72 h-72" />
          <div className="hero-orb-secondary bottom-20 right-10 w-96 h-96" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left: Typography */}
              <motion.div
                initial="initial"
                animate="animate"
                variants={staggerContainer}
                className="text-center lg:text-left"
              >
                <motion.p
                  variants={fadeInUp}
                  className="text-accent text-sm font-semibold uppercase tracking-widest mb-4"
                >
                  A Podcast
                </motion.p>

                <motion.h1
                  variants={fadeInUp}
                  className="font-display text-display-lg md:text-display-xl text-[var(--text-primary)] mb-2"
                >
                  News from the
                </motion.h1>

                <motion.div
                  variants={fadeInUp}
                  className="relative inline-block"
                >
                  <span className="font-display text-display-xl md:text-[6rem] lg:text-[7rem] text-gradient leading-none">
                    Pew
                  </span>
                  {/* Animated underline */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 0.8, ease: 'easeOut' }}
                    className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-subtle rounded-full origin-left"
                  />
                </motion.div>

                <motion.p
                  variants={fadeInUp}
                  className="mt-8 text-lg text-[var(--text-secondary)] max-w-lg mx-auto lg:mx-0"
                >
                  <strong className="text-[var(--text-primary)]">Theology with teeth.</strong>
                  <br />
                  <em>Unmasking the deception of the age with the uncompromised power of Scripture.</em>
                </motion.p>

                <motion.div
                  variants={fadeInUp}
                  className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start"
                >
                  <GradientButton
                    to="/episodes"
                    icon={<ArrowRight size={18} />}
                  >
                    Browse Episodes
                  </GradientButton>

                  <GradientButton
                    variant="outline"
                    href={socialMedia.spotify}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-spotify mr-2" />
                    Listen on Spotify
                  </GradientButton>
                </motion.div>
              </motion.div>

              {/* Right: Producer Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden">
                  {/* Gradient border effect */}
                  <div className="absolute inset-0 bg-gradient-primary p-[2px] rounded-2xl">
                    <div className="absolute inset-[2px] bg-[var(--bg-primary)] rounded-2xl" />
                  </div>

                  <img
                    src="/assets/images/David.jpeg"
                    alt={`J. David Wyatt, producer and writer of ${podcastInfo.name}`}
                    className="relative w-full aspect-[4/3] object-cover rounded-2xl"
                  />

                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-primary/60 via-transparent to-transparent rounded-2xl" />
                </div>

                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1, duration: 0.5 }}
                  className="absolute -bottom-4 -left-4 md:left-8 bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-xl px-4 py-3 shadow-card"
                >
                  <p className="text-xs text-[var(--text-secondary)] uppercase tracking-wider">New Episode</p>
                  <p className="text-sm font-semibold text-[var(--text-primary)]">Every Week</p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Latest Episode Section */}
        <section className="relative py-20 bg-[var(--bg-secondary)] overflow-hidden">
          {/* Ambient glow */}
          <div className="ambient-orb-tertiary top-10 right-10 w-64 h-64" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-8">
                <h2 className="font-display text-display-sm text-[var(--text-primary)]">Latest Episode</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-accent/50 to-transparent" />
              </div>

              {loading ? (
                <div className="gradient-border p-8">
                  <div className="animate-pulse space-y-4">
                    <div className="h-6 bg-[var(--bg-tertiary)] rounded w-3/4" />
                    <div className="h-4 bg-[var(--bg-tertiary)] rounded w-1/2" />
                    <div className="h-20 bg-[var(--bg-tertiary)] rounded" />
                  </div>
                </div>
              ) : latestEpisode ? (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="gradient-border"
                >
                  <div className="relative p-6 md:p-8">
                    <div className="grid md:grid-cols-[200px_1fr] gap-6 md:gap-8">
                      {/* Episode Thumbnail */}
                      <div className="flex-shrink-0">
                        {latestEpisode.thumbnail ? (
                          <Link to={`/episode/${latestEpisode.id}`} className="block">
                            <div className="relative aspect-square rounded-xl overflow-hidden bg-[var(--bg-tertiary)] group">
                              <img
                                src={latestEpisode.thumbnail}
                                alt={latestEpisode.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              {/* Play overlay on hover */}
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center">
                                  <Play size={24} fill="white" className="text-white ml-1" />
                                </div>
                              </div>
                              {/* Episode number badge */}
                              <div className="absolute top-2 left-2 bg-black/70 text-white text-xs font-mono px-2 py-1 rounded">
                                EP {latestEpisode.episodeNumber || ''}
                              </div>
                            </div>
                          </Link>
                        ) : (
                          <div className="aspect-square rounded-xl bg-[var(--bg-tertiary)] flex items-center justify-center">
                            <Play size={32} className="text-[var(--text-muted)]" />
                          </div>
                        )}
                        {/* Duration below thumbnail */}
                        <div className="flex items-center justify-center gap-2 text-[var(--text-muted)] text-sm mt-3">
                          <Clock size={14} />
                          <span>{latestEpisode.duration || latestEpisode.date}</span>
                        </div>
                      </div>

                      {/* Episode Content */}
                      <div className="space-y-4">
                        <Link to={`/episode/${latestEpisode.id}`}>
                          <h3 className="font-display text-2xl md:text-3xl text-[var(--text-primary)] hover:text-accent transition-colors">
                            {latestEpisode.title}
                          </h3>
                        </Link>

                        <p className="text-[var(--text-secondary)] line-clamp-3">
                          {latestEpisode.description}
                        </p>

                        {/* Tags */}
                        {latestEpisode.tags && latestEpisode.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {latestEpisode.tags.map(tag => (
                              <TagPill key={tag} tag={tag} size="small" />
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap items-center gap-4 pt-4">
                          <GradientButton
                            to={`/episode/${latestEpisode.id}`}
                            icon={<Play size={18} fill="currentColor" />}
                            iconPosition="left"
                          >
                            Play Episode
                          </GradientButton>

                          {/* Platform Links */}
                          <div className="flex items-center gap-3">
                            {socialMedia.applePodcasts && (
                              <a
                                href={socialMedia.applePodcasts}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-[var(--bg-tertiary)] hover:bg-[var(--bg-elevated)] rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                              >
                                <i className="fab fa-apple" />
                                <span className="hidden sm:inline">Apple</span>
                              </a>
                            )}
                            {socialMedia.spotify && (
                              <a
                                href={socialMedia.spotify}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-2 bg-[#1DB954] hover:bg-[#1ed760] rounded-lg text-sm text-white transition-colors"
                              >
                                <i className="fab fa-spotify" />
                                <span className="hidden sm:inline">Spotify</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : null}
            </motion.div>
          </div>
        </section>

        {/* Support Section */}
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-primary p-8 md:p-12"
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex-shrink-0"
                >
                  <img
                    src="/assets/images/forgiving_like_god_free_download.jpeg"
                    alt="Forgiving Like God - Free Download"
                    className="w-48 md:w-56 lg:w-64 rounded-2xl shadow-2xl"
                  />
                </motion.div>

                {/* Content */}
                <div className="text-center lg:text-left max-w-xl">
                  <motion.div
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ type: 'spring', delay: 0.2 }}
                    className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white/10 mb-4"
                  >
                    <Shield size={24} className="text-white" />
                  </motion.div>

                  <h2 className="font-display text-display-sm md:text-display-md text-white mb-4">
                    Truth for the Mind. Healing for the Soul.
                  </h2>

                  <p className="text-white/80 text-lg mb-6">
                    The world is loud. We are building a sanctuary of sound and scripture. Join us in <strong>The Upper Room</strong> for unposted sermons, unreleased worship demos, and ad-free episodes. Help us minister to a culture that needs both clarity and comfort.
                  </p>

                  <div className="flex justify-center lg:justify-start">
                    <motion.a
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      href="https://patreon.com/JDavidWyatt?utm_medium=unknown&utm_source=join_link&utm_campaign=creatorshare_creator&utm_content=copyLink"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-dark-primary font-semibold rounded-xl hover:bg-gray-100 transition-colors text-lg"
                    >
                      <i className="fab fa-patreon" />
                      Join the Upper Room on Patreon
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="relative py-20 bg-[var(--bg-secondary)] overflow-hidden">
          {/* Ambient glows */}
          <div className="ambient-orb-tertiary top-20 left-0 w-72 h-72" />
          <div className="ambient-orb-tertiary bottom-10 right-10 w-48 h-48" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-4 mb-12">
                <h2 className="font-display text-display-sm text-[var(--text-primary)]">What People Say</h2>
                <div className="flex-1 h-px bg-gradient-to-r from-accent/50 to-transparent" />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="card-elevated glow-card p-6"
                  >
                    {/* Quote mark */}
                    <div className="text-accent/30 text-6xl font-display leading-none mb-4">"</div>

                    <p className="text-[var(--text-secondary)] italic mb-4">
                      {testimonial.quote}
                    </p>

                    <p className="text-[var(--text-primary)] font-semibold text-sm">
                      {testimonial.author}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 overflow-hidden">
          {/* Ambient glow */}
          <div className="ambient-orb-primary top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 opacity-50" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h2 className="font-display text-display-sm md:text-display-md text-[var(--text-primary)] mb-4">
                Ready to Dive In?
              </h2>
              <p className="text-[var(--text-secondary)] text-lg mb-8 max-w-xl mx-auto">
                Explore our complete archive of episodes covering faith, culture, and current events.
              </p>
              <GradientButton
                to="/episodes"
                size="large"
                icon={<ChevronRight size={20} />}
              >
                Browse All Episodes
              </GradientButton>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  </>
  );
};

export default HomePage;
